import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { LockKeyhole } from 'lucide-react';
import { useAdminContext } from '../../context/AdminContext';
import ArabAliLogo from '../../components/ArabAliLogo';
import PrimaryActionButton from '../../admin/components/buttons/PrimaryActionButton';
import TextField from '../../admin/components/fields/TextField';
import AdminPanel from '../../admin/components/shared/AdminPanel';

const AdminLoginPage: React.FC = () => {
  const { login, sessionStatus } = useAdminContext();
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const showPreviewNote = import.meta.env.DEV && !import.meta.env.VITE_FUNCTIONS_BASE_URL;

  if (sessionStatus === 'authenticated') {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await login(passcode);
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : 'Could not log in to admin');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="admin-shell flex min-h-screen items-center justify-center px-4 py-10">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(246,199,90,0.16),transparent_32%),radial-gradient(circle_at_bottom_left,rgba(171,41,27,0.18),transparent_36%)]" />
      <AdminPanel className="relative w-full max-w-5xl overflow-hidden p-0">
        <div className="grid lg:grid-cols-[0.95fr_1.05fr]">
          <div className="border-b border-white/10 p-8 lg:border-b-0 lg:border-r lg:p-10">
            <ArabAliLogo badgeLabel="Restaurant CMS" />
            <div className="mt-10 inline-flex rounded-full border border-amber-300/20 bg-amber-400/8 px-4 py-1 text-[11px] uppercase tracking-[0.35em] text-amber-100">
              Admin CMS
            </div>
            <h1 className="mt-6 font-heading text-4xl font-semibold text-white sm:text-5xl">
              Simple backstage control for the live restaurant menu.
            </h1>
            <p className="mt-5 max-w-xl text-base leading-8 text-white/60">
              Use the shared passcode to edit sections, items, prices, contact details, colors, logos, and food images without touching code.
            </p>
            <div className="mt-8 rounded-[28px] border border-white/10 bg-white/5 p-5">
              <div className="flex items-center gap-3 text-amber-200">
                <LockKeyhole className="h-5 w-5" />
                <span className="text-sm font-semibold uppercase tracking-[0.24em]">Passcode Gate</span>
              </div>
              <p className="mt-3 text-sm leading-7 text-white/55">
                This admin area is protected by a shared passcode and stores draft and published content through the live backend workspace.
              </p>
            </div>
          </div>

          <div className="p-8 lg:p-10">
            <div className="text-[11px] uppercase tracking-[0.35em] text-amber-100/45">Login</div>
            <h2 className="mt-3 text-3xl font-semibold text-white">Enter the admin passcode</h2>
            <p className="mt-3 text-sm leading-7 text-white/55">
              Once signed in, you’ll land on the shared draft workspace for categories, items, settings, and media.
            </p>

            {showPreviewNote && (
              <div className="mt-5 rounded-2xl border border-amber-300/20 bg-amber-400/10 px-4 py-3 text-sm leading-7 text-amber-50/90">
                This local preview shows the admin design. To use the real passcode login and save changes, run the app with the Cloudflare Worker API or connect `VITE_FUNCTIONS_BASE_URL`.
              </div>
            )}

            <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
              <TextField
                label="Admin Passcode"
                value={passcode}
                onChange={setPasscode}
                placeholder="Enter shared passcode"
                required
              />

              {error && (
                <div className="rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm text-red-100">
                  {error}
                </div>
              )}

              <PrimaryActionButton type="submit" fullWidth disabled={isSubmitting || !passcode.trim()}>
                {isSubmitting ? 'Opening Backstage...' : 'Open Admin Workspace'}
              </PrimaryActionButton>
            </form>
          </div>
        </div>
      </AdminPanel>
    </div>
  );
};

export default AdminLoginPage;
